import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft, BookOpen, Music, Camera, Map } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const NotFoundPage = () => {
  const { isDark } = useTheme();

  useEffect(() => {
    // Set SEO meta tags for 404 page
    document.title = '404 - Page Not Found | Alan Shteynberg';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'The page you are looking for could not be found. Navigate back to Alan Shteynberg\'s portfolio to explore projects, blog posts, and more.');
    }

    // Add noindex meta tag for 404 pages (SEO best practice)
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, follow';

    // Cleanup on unmount
    return () => {
      document.title = 'Alan was here.';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'A product of emergent.sh');
      }
      if (robotsMeta) {
        robotsMeta.content = 'index, follow';
      }
    };
  }, []);

  const quickLinks = [
    { icon: Home, label: 'Home', path: '/', description: 'Back to the main page' },
    { icon: BookOpen, label: 'Reliquaries', path: '/blog', description: 'Read watercolor banter' },
    { icon: Music, label: 'Music', path: '/music', description: 'Listen to curated playlists' },
    { icon: Camera, label: 'Media', path: '/media', description: 'Photography & videography' },
    { icon: Map, label: 'Roadmap', path: '/roadmap', description: 'Journey timeline' },
  ];

  return (
    <div className="not-found-page" data-testid="404-page">
      <div className="not-found-container">
        {/* Error Code */}
        <div className="error-code">
          <span className="code-4">4</span>
          <span className="code-0">0</span>
          <span className="code-4">4</span>
        </div>

        {/* Main Message */}
        <h1 className="error-title">Page Not Found</h1>
        <p className="error-description">
          The page you're looking for doesn't exist, has been moved, or is temporarily unavailable.
        </p>

        {/* Search Suggestion */}
        <div className="search-suggestion">
          <Search size={18} />
          <span>Try checking the URL for typos or use the links below to navigate.</span>
        </div>

        {/* Quick Links */}
        <nav className="quick-links" aria-label="Quick navigation">
          <h2 className="quick-links-title">Quick Links</h2>
          <div className="links-grid">
            {quickLinks.map((link) => (
              <Link 
                key={link.path} 
                to={link.path} 
                className="quick-link-card"
                data-testid={`404-link-${link.label.toLowerCase()}`}
              >
                <link.icon size={24} className="link-icon" />
                <div className="link-content">
                  <span className="link-label">{link.label}</span>
                  <span className="link-description">{link.description}</span>
                </div>
              </Link>
            ))}
          </div>
        </nav>

        {/* Back Button */}
        <button 
          onClick={() => window.history.back()} 
          className="back-button"
          data-testid="404-go-back"
        >
          <ArrowLeft size={18} />
          Go Back
        </button>
      </div>

      <style jsx>{`
        .not-found-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: ${isDark ? '#151312' : '#f8fafc'};
          transition: background-color 0.3s ease;
        }

        .not-found-container {
          max-width: 600px;
          width: 100%;
          text-align: center;
        }

        .error-code {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 24px;
        }

        .error-code span {
          font-family: 'Inter', sans-serif;
          font-size: clamp(80px, 20vw, 140px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.05em;
        }

        .code-4 {
          color: ${isDark ? '#fff' : '#151312'};
          transition: color 0.3s ease;
        }

        .code-0 {
          color: #f46c38;
        }

        .error-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(24px, 5vw, 36px);
          font-weight: 700;
          color: ${isDark ? '#fff' : '#151312'};
          margin: 0 0 16px 0;
          transition: color 0.3s ease;
        }

        .error-description {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: ${isDark ? '#998f8f' : '#64748b'};
          line-height: 1.6;
          margin: 0 0 24px 0;
          transition: color 0.3s ease;
        }

        .search-suggestion {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          background: ${isDark ? 'rgba(244, 108, 56, 0.1)' : 'rgba(244, 108, 56, 0.08)'};
          border-radius: 8px;
          margin-bottom: 40px;
          color: ${isDark ? '#f46c38' : '#ea580c'};
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          transition: background-color 0.3s ease;
        }

        .quick-links {
          margin-bottom: 32px;
        }

        .quick-links-title {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: ${isDark ? '#998f8f' : '#64748b'};
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin: 0 0 16px 0;
          transition: color 0.3s ease;
        }

        .links-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
        }

        .quick-link-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: ${isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .quick-link-card:hover {
          background: ${isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
          border-color: ${isDark ? 'rgba(244, 108, 56, 0.3)' : 'rgba(244, 108, 56, 0.4)'};
          transform: translateY(-2px);
        }

        .link-icon {
          color: #f46c38;
          flex-shrink: 0;
        }

        .link-content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }

        .link-label {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: ${isDark ? '#fff' : '#151312'};
          transition: color 0.3s ease;
        }

        .link-description {
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          color: ${isDark ? '#998f8f' : '#64748b'};
          transition: color 0.3s ease;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#151312' : '#fff'};
          background: ${isDark ? '#fff' : '#151312'};
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 32px;
        }

        .back-button:hover {
          background: #f46c38;
          color: #fff;
        }

        @media (max-width: 600px) {
          .not-found-page {
            padding: 24px 16px;
          }

          .search-suggestion {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .links-grid {
            grid-template-columns: 1fr;
          }

          .quick-link-card {
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default NotFoundPage;
